import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Un quad, un shader, du néon.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'poincare',
  family: 'shader',
  danceability: 0.80,
  requiresTier: 0,
  energyLevel: 0.74,
  bpmMin: 110,
  bpmMax: 150,
  movement: 0.75,
  tonality: 0.55,
  enabled: true
};

// POINCARÉ : un pavage {p, q} du plan hyperbolique dans le disque de Poincaré,
// comme les « Limite circulaire » d'Escher mais en néon. Sur chaque temps, une
// translation hyperbolique fait glisser tout le pavage d'exactement une tuile :
// on avance dans un monde infini courbé. Techno, psytrance, progressive :
// preset party de vdjLook (séparation RGB sur le temps, flash au drop, confort).
//
// LE PAVAGE, dans le fragment shader. Le triangle fondamental a son sommet à
// l'origine (angle π/p), un sommet de tuile à l'angle π/q et un angle droit au
// milieu d'arête ; ses côtés sont l'axe réel, la droite d'angle π/p et le
// cercle miroir orthogonal au disque, de centre c = cos(π/q)/√(cos²(π/p) −
// sin²(π/q)) et de rayon √(c² − 1). Chaque pixel est RAMENÉ dans ce triangle
// par réflexions itérées (rotation d'un multiple de 2π/p, réflexion sur l'axe,
// inversion dans le cercle), 30 fois au plus avec sortie anticipée. Le nombre
// d'inversions est la GÉNÉRATION (distance à la tuile centrale), la parité des
// réflexions alterne les tons : les tuiles sont congruentes par construction,
// les raccords exacts.
//
// LES ARÊTES sont les images du cercle miroir ; e = |z_f − C| − r est continu
// sur tout le disque (symétrique sous le repliement), donc e / fwidth(e) est la
// distance EN PIXELS à l'arête la plus proche (R1 : largeur constante à
// l'écran). La taille locale d'une tuile en pixels, ρ_in / fwidth(ρ), amincit
// puis fond les arêtes vers le bord et ramène les remplissages à leur ton moyen
// sous 3 px : rien ne fourmille. Motif intérieur (anneau, point, rayons) tracé
// dans les coordonnées repliées : il suit chaque tuile à son échelle.
//
// LE MOUVEMENT : sur chaque temps, z → e^{iφ}(z − a)/(1 − ā z) avec a qui va de
// 0 au centre de la tuile voisine (1/c dans la direction k) le long de la
// géodésique, φ de 0 à la rotation (0 ou π/p, calculée une fois) qui fait de la
// composée une SYMÉTRIE du pavage : à la fin du temps la transformation se
// remplace par l'identité sans saut, et la tuile voisine est au centre, à la
// génération 0. La direction change sur la mesure. Montée : translations deux
// fois plus rapides et glissement parabolique (horocyclique) vers un point du
// bord. Drop : flash et retournement du disque (ρ' = K²/ρ en distance
// hyperbolique, le centre et le bord s'échangent) aller-retour sur un temps.
// Phrase : nouveau pavage en fondu croisé d'un temps, nouvelle palette. Scène
// (lib/scene-clock.js, 45 s) : un pavage par scène au moins.

const TAU = Math.PI * 2;
const TILINGS = [[7, 3], [5, 4], [4, 5], [6, 4], [8, 3]];
const DISC_R = 0.92;                 // of the half height: 92 % of the height
const MAX_ITER = 30;
const TIERS = { 0: { iter: 18, motif: 0 }, 1: { iter: 30, motif: 1 }, 2: { iter: 30, motif: 1 } };
const NO_BPM = 126;
const SCENE_S = 45;

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
const smoother = (x) => { const t = Math.min(1, Math.max(0, x)); return t * t * t * (t * (6 * t - 15) + 10); };
function hashStr(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h >>> 0; }

// --- complex numbers and Möbius maps as 2x2 complex matrices ---------------------------
const C = {
  mul: (a, b) => [a[0] * b[0] - a[1] * b[1], a[0] * b[1] + a[1] * b[0]],
  add: (a, b) => [a[0] + b[0], a[1] + b[1]],
  sub: (a, b) => [a[0] - b[0], a[1] - b[1]],
  conj: (a) => [a[0], -a[1]],
  div: (a, b) => { const d = b[0] * b[0] + b[1] * b[1]; return [(a[0] * b[0] + a[1] * b[1]) / d, (a[1] * b[0] - a[0] * b[1]) / d]; },
  exp: (th) => [Math.cos(th), Math.sin(th)],
  arg: (a) => Math.atan2(a[1], a[0])
};
// M = [[a, b], [c, d]], z -> (a z + b) / (c z + d)
const M = {
  id: () => [[1, 0], [0, 0], [0, 0], [1, 0]],
  mul: (m, n) => [
    C.add(C.mul(m[0], n[0]), C.mul(m[1], n[2])), C.add(C.mul(m[0], n[1]), C.mul(m[1], n[3])),
    C.add(C.mul(m[2], n[0]), C.mul(m[3], n[2])), C.add(C.mul(m[2], n[1]), C.mul(m[3], n[3]))
  ],
  apply: (m, z) => C.div(C.add(C.mul(m[0], z), m[1]), C.add(C.mul(m[2], z), m[3])),
  // the hyperbolic translation taking a to the origin: (z - a) / (1 - conj(a) z)
  trans: (a) => [[1, 0], [-a[0], -a[1]], [-a[0], a[1]], [1, 0]],
  rot: (th) => [C.exp(th), [0, 0], [0, 0], [1, 0]],
  // the parabolic glide of amount t fixing the boundary point e^{i psi}: conjugate the
  // upper-half-plane shift w -> w + t by the Cayley map and the rotation
  parabolic: (t, psi) => {
    const cay = [[0, 1], [0, 1], [-1, 0], [1, 0]];           // z -> i(1 + z)/(1 - z)
    const inv = [[1, 0], [0, -1], [1, 0], [0, 1]];           // w -> (w - i)/(w + i)
    const shift = [[1, 0], [t, 0], [0, 0], [1, 0]];
    return M.mul(M.rot(psi), M.mul(inv, M.mul(shift, M.mul(cay, M.rot(-psi)))));
  }
};

// the geometry of {p, q}: the mirror circle, the vertex distance, the inradius, the neighbour
// centre, and the rotation that makes "translate one tile" a symmetry of the tiling
export function tilingGeometry(p, q) {
  const cp = Math.cos(Math.PI / p), sq = Math.sin(Math.PI / q);
  const c = Math.cos(Math.PI / q) / Math.sqrt(cp * cp - sq * sq);
  const r = Math.sqrt(c * c - 1);
  const tv = c * cp - Math.sqrt(c * c * cp * cp - 1);      // the tile vertex, at angle pi/p
  const rhoIn = 2 * Math.atanh(c - r), rhoV = 2 * Math.atanh(tv);
  const nb = 1 / c;                                        // the neighbour's centre across the edge at angle 0
  // after T_nb the tiling is itself rotated by 0 or pi/p: measure on the shared vertex
  const v = [tv * Math.cos(Math.PI / p), tv * Math.sin(Math.PI / p)];
  const w = M.apply(M.trans([nb, 0]), v);
  const step = TAU / p;
  let rot = (Math.PI / p - C.arg(w)) % step; if (rot < 0) rot += step; if (rot > step / 2) rot -= step;
  if (Math.abs(rot) < 1e-6) rot = 0;
  return { p, q, c, r, tv, rhoIn, rhoV, nb, rot, D: 2 * Math.atanh(nb) };
}

// --- GLSL ------------------------------------------------------------------------------
const vertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const fragmentShader = `
  precision highp float;
  #define PI 3.14159265
  #define MAXI ${MAX_ITER}
  uniform vec2 uMa, uMb, uMc, uMd;        // the Möbius map (a z + b) / (c z + d)
  uniform vec2 uIa, uIb, uIc, uId;        // its inverse: tiling coordinates back to the screen
  uniform vec2 uSa, uSb, uSc, uSd;        // tiling coordinates to the ABSOLUTE ones (the beats' symmetries)
  uniform vec2 uArr;                      // the centre of the tile arriving at the screen centre
  uniform vec4 uTil;                      // p, c, r, rhoIn
  uniform vec4 uTil2;                     // the next tiling during a crossfade
  uniform float uMix, uIter, uMotif, uMotifType;
  uniform float uFlip, uFlipK;            // the drop's inside-out, 0..1, and K
  uniform vec3 uBands;                    // low, mid, high
  uniform float uFlash;                   // the arriving tile's edge flash
  uniform float uRingPulse, uGain, uT, uDebug;
  uniform vec3 uTone0, uTone1, uTone2, uMean;
  uniform float uAspect, uPx, uDiscR;
  uniform vec2 uStage, uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  vec2 cmul(vec2 a, vec2 b) { return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x); }
  vec2 cdiv(vec2 a, vec2 b) { float d = dot(b, b); return vec2(a.x * b.x + a.y * b.y, a.y * b.x - a.x * b.y) / d; }
  vec2 rot(vec2 z, float t) { float c = cos(t), s = sin(t); return vec2(c * z.x - s * z.y, s * z.x + c * z.y); }
  vec2 mob(vec2 a, vec2 b, vec2 c, vec2 d, vec2 z) { return cdiv(cmul(a, z) + b, cmul(c, z) + d); }
  // the fold into the fundamental triangle: rotations (free), reflections on the axis,
  // inversions in the mirror circle (gen = generation). The derivative of the whole chain is
  // carried along as (lam, fl): v -> lam * (fl ? conj v : v), one complex multiply per step.
  vec2 fold(vec2 z, vec4 T, out float gen, out vec2 lam, out float fl) {
    float p = T.x, cc = T.y, rr = T.z, sw = 2.0 * PI / p;
    float ang = atan(z.y, z.x);
    float k = floor((ang + 0.5 * sw) / sw);
    z = rot(z, -k * sw);
    lam = vec2(cos(-k * sw), sin(-k * sw)); fl = 0.0; gen = 0.0;
    for (int i = 0; i < MAXI; i++) {
      if (float(i) >= uIter) break;
      bool moved = false;
      if (z.y < 0.0) { z.y = -z.y; lam.y = -lam.y; fl = 1.0 - fl; moved = true; }
      vec2 d = z - vec2(cc, 0.0);
      float dd = dot(d, d);
      if (dd < rr * rr) {
        // z' = C + r^2 / conj(z - C): derivative -r^2 / conj((z - C)^2), anti-conformal
        vec2 d2 = cmul(d, d);
        vec2 lz = -rr * rr * vec2(d2.x, d2.y) / dot(d2, d2);        // = -r^2 / conj(d^2)
        lam = cmul(lz, vec2(lam.x, -lam.y)); fl = 1.0 - fl;
        z = vec2(cc, 0.0) + d * (rr * rr / dd);
        gen += 1.0;
        ang = atan(z.y, z.x); k = floor((ang + 0.5 * sw) / sw); z = rot(z, -k * sw);
        lam = rot(lam, -k * sw);
      } else if (!moved) break;
    }
    return z;
  }
  // the centre of the tile containing z0: at hyperbolic distance rho from z0 along the direction
  // that the fold's derivative maps onto "toward the origin" at zf
  vec2 tileCentre(vec2 z0, vec2 zf, float rho, vec2 lam, float fl) {
    vec2 v = cdiv(-zf, lam);
    if (fl > 0.5) v.y = -v.y;
    float lv = length(v);
    if (lv < 1e-7) return z0;
    vec2 e = v / lv * tanh(0.5 * rho);
    return cdiv(e + z0, vec2(1.0, 0.0) + cmul(vec2(z0.x, -z0.y), e));
  }
  // one tiling's colour at the disc point z0; e: the edge field, rho: distance to the tile centre
  vec3 tiling(vec2 z0, vec4 T, out float e, out float rho, out float gen, out vec2 cen) {
    vec2 lam; float fl;
    vec2 zf = fold(z0, T, gen, lam, fl);
    e = length(zf - vec2(T.y, 0.0)) - T.z;
    rho = 2.0 * atanh(min(length(zf), 0.9999));
    float tilePx = T.w / max(fwidth(rho), 1e-6);              // the tile's inradius in pixels
    float dpx = abs(e) / max(fwidth(e), 1e-6);                // pixels to the nearest edge
    cen = tileCentre(z0, zf, rho, lam, fl);
    // the tile's centre on screen (its hyperbolic distance from the disc centre lights it) and in
    // absolute coordinates (its tone travels with it from beat to beat)
    vec2 cs = mob(uIa, uIb, uIc, uId, cen);
    float rhoC = 2.0 * atanh(min(length(cs), 0.9999));
    vec2 ca = mob(uSa, uSb, uSc, uSd, cen);
    float g = sin(ca.x * 23.0 + 0.7) + sin(ca.y * 19.0 + ca.x * 7.0);     // neighbours (0.5 apart) land on different tones
    float tone = mod(floor(3.0 * fract(g * 0.43 + 0.5)), 3.0);            // three tones, evenly drawn
    vec3 fill = tone < 0.5 ? uTone0 : tone < 1.5 ? uTone1 : uTone2;
    float lit = 0.13 + 0.32 * uBands.x * exp(-rhoC / 0.9) + 0.32 * uBands.z * smoothstep(1.5, 4.5, rhoC) + 0.08 * uBands.y;
    vec3 col = fill * lit;
    if (uDebug > 0.5) return vec3(lit * 2.0) + vec3(tone * 0.1, 0.0, 0.0);
    // the motif, at the tile's own scale
    if (uMotif > 0.5) {
      vec3 mcol = (tone < 0.5 ? uTone2 : tone < 1.5 ? uTone0 : uTone1) * 0.55;
      float rIn = T.w;
      if (uMotifType < 0.5 || uMotifType > 1.5) {
        float m = abs(rho - 0.55 * rIn) / max(fwidth(rho), 1e-6);
        col += mcol * (1.0 - smoothstep(0.5, 1.6, m)) * 0.7 * smoothstep(4.0, 12.0, tilePx);
      }
      if (uMotifType > 0.5 && uMotifType < 1.5) {
        float sp = abs(zf.y) / max(fwidth(zf.y), 1e-6);        // the spoke to the edge midpoint
        float on = smoothstep(0.25 * rIn, 0.35 * rIn, rho) * (1.0 - smoothstep(0.75 * rIn, 0.85 * rIn, rho));
        col += mcol * (1.0 - smoothstep(0.5, 1.6, sp)) * on * 0.7 * smoothstep(4.0, 12.0, tilePx);
      }
      if (uMotifType > 1.5) col += mcol * (1.0 - smoothstep(0.18 * rIn, 0.24 * rIn, rho)) * 0.5 * smoothstep(4.0, 12.0, tilePx);
    }
    // small tiles sink into their mean tone: no shimmer at the rim
    float tileK = smoothstep(2.0, 6.0, tilePx);
    col = mix(uMean * 0.07, col, tileK);
    // the neon edge (R1): a white core, a coloured sheath, a halo; thinner then gone as tiles shrink
    float wf = smoothstep(3.0, 16.0, tilePx);
    float edgeK = smoothstep(1.5, 5.0, tilePx);
    vec3 ecol = mix(uTone0, uTone1, smoothstep(0.8, 2.5, rhoC));
    // R1: a core of 2 px at most (the only thing over the bloom threshold), a sheath to 3 px
    // at 0.7, a halo; dense edges near the rim keep only their sheath (summed halos veiled)
    float core = (1.0 - smoothstep(0.5 * wf, 0.5 + 1.1 * wf, dpx)) * wf;
    float sheath = (1.0 - smoothstep(0.5 + 1.1 * wf, 1.6 + 1.9 * wf, dpx)) * smoothstep(2.5, 9.0, tilePx);
    float halo = exp(-dpx / 6.0) * 0.22 * smoothstep(4.0, 14.0, tilePx);
    // the arriving tile's edges flash on the beat; the core sits just over the bloom threshold
    // (thousands of cores at 0.95 fed a grey veil over the whole frame)
    float flash = uFlash * (1.0 - smoothstep(0.02, 0.04, length(cen - uArr)));
    col += ecol * (sheath * 0.6 + halo) * edgeK * (1.0 + 1.5 * flash) + vec3(1.0, 0.97, 0.92) * core * edgeK * (0.80 + 0.5 * flash);
    return col;
  }
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uStage;
    vec2 z0 = s / uDiscR;
    float rr = length(z0);
    // the Möbius map, then the drop's inside-out in hyperbolic distance
    vec2 zc = clamp(rr, 0.0, 0.9995) / max(rr, 1e-6) * z0;
    vec2 z = cdiv(cmul(uMa, zc) + uMb, cmul(uMc, zc) + uMd);
    if (uFlip > 0.0) {
      float lz = min(length(z), 0.9995);
      float rho = max(2.0 * atanh(lz), 1e-3);
      float rho2 = exp(mix(log(rho), 2.0 * log(uFlipK) - log(rho), uFlip));
      z = z / max(lz, 1e-6) * tanh(0.5 * min(rho2, 16.0));
    }
    float e, rho, gen; vec2 cen;
    vec3 col = tiling(z, uTil, e, rho, gen, cen);
    if (uMix > 0.0) {
      float e2, rho2, gen2; vec2 cen2;
      vec3 col2 = tiling(z, uTil2, e2, rho2, gen2, cen2);
      col = mix(col, col2, uMix);
    }
    // outside: black, a thin neon ring that pulses on the beat
    float dR = (rr - 1.0) * uDiscR / uPx;
    float inside = 1.0 - smoothstep(-0.7, 0.7, dR);
    float ring = (1.0 - smoothstep(0.5, 1.3, abs(dR - 1.5))) * (0.5 + 0.5 * uRingPulse);
    float rhalo = exp(-abs(dR - 1.5) / 6.0) * 0.18 * (0.5 + 0.5 * uRingPulse);
    col = col * inside + uTone0 * (ring * 0.7 + rhalo) + vec3(1.0, 0.97, 0.92) * ring * 0.5;
    // the cover's mask follows the cover (R19); the disc stays on the stage centre
    vec2 qq = abs((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uSafeCenter) - uSafeHalfSize;
    float dz = length(max(qq, 0.0)) + min(max(qq.x, qq.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, dz));
    gl_FragColor = vec4(col * uGain, 1.0);
  }
`;

export class PoincareVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIERS[o.qualityTier] ? o.qualityTier : 1;
    this._T = TIERS[this._tier];
    const f = urlParam('hyp');
    if (f) { const [p, q] = f.split(',').map((x) => parseInt(x, 10)); if (p >= 3 && q >= 3 && 1 / p + 1 / q < 0.5) this._forceTiling = [p, q]; }
    this._freeze = urlParam('hypfreeze') === '1';
    const it = parseInt(urlParam('hypiter'), 10);
    this._forceIter = Number.isFinite(it) ? Math.max(1, Math.min(MAX_ITER, it)) : null;
    this._adapt = new Adapt();
    const gr = parseFloat(urlParam('hypgrain'));
    this._post = new VdjPost({ bloomStrength: 0.18, bloomThreshold: 0.78, grain: Number.isFinite(gr) ? gr : 0.012, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    if (urlParam('hypprobe') === '1' && typeof window !== 'undefined') window.__poincare = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    this._U = {
      uMa: { value: new THREE.Vector2(1, 0) }, uMb: { value: new THREE.Vector2() }, uMc: { value: new THREE.Vector2() }, uMd: { value: new THREE.Vector2(1, 0) },
      uIa: { value: new THREE.Vector2(1, 0) }, uIb: { value: new THREE.Vector2() }, uIc: { value: new THREE.Vector2() }, uId: { value: new THREE.Vector2(1, 0) },
      uSa: { value: new THREE.Vector2(1, 0) }, uSb: { value: new THREE.Vector2() }, uSc: { value: new THREE.Vector2() }, uSd: { value: new THREE.Vector2(1, 0) },
      uArr: { value: new THREE.Vector2(9, 9) },
      uTil: { value: new THREE.Vector4() }, uTil2: { value: new THREE.Vector4() },
      uMix: { value: 0 }, uIter: { value: this._forceIter || this._T.iter }, uMotif: { value: this._T.motif }, uMotifType: { value: 0 },
      uFlip: { value: 0 }, uFlipK: { value: 1 },
      uBands: { value: new THREE.Vector3(0.3, 0.3, 0.3) }, uFlash: { value: 0 },
      uRingPulse: { value: 0 }, uGain: { value: 1 }, uT: { value: 0 }, uDebug: { value: urlParam('hypdebug') === '1' ? 1 : 0 },
      uTone0: { value: new THREE.Color(0.1, 0.8, 1) }, uTone1: { value: new THREE.Color(1, 0.2, 0.6) }, uTone2: { value: new THREE.Color(0.6, 0.3, 1) }, uMean: { value: new THREE.Color(0.5, 0.4, 0.8) },
      uAspect: safeZoneUniforms.uAspect, uPx: { value: 2 / 1080 }, uDiscR: { value: DISC_R }, uStage: safeZoneUniforms.uStageCenter,
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._U, vertexShader, fragmentShader, depthTest: false, depthWrite: false }));
    this._mesh.frustumCulled = false; this._mesh.renderOrder = -1000;
    const self = this;
    this._mesh.onBeforeRender = function (renderer) {
      const rt = renderer.getRenderTarget();
      const h = rt ? rt.height : renderer.getDrawingBufferSize(_size).y;
      self._U.uPx.value = 2 / h;
      // 92 % of the height, or the width less 5 % margins in portrait (R6)
      self._U.uDiscR.value = Math.min(DISC_R, (self._U.uAspect.value || 1) * 0.95 - 0.03);
    };
    scene.add(this._mesh);

    this._seed = 7; this._trackSeen = undefined;
    this._time = 0; this._freePhase = 0; this._beats = 0;
    this._tilIdx = 0; this._setTiling(this._forceTiling || TILINGS[0]);
    this._next = null; this._mixT = -1;
    this._dir = 0; this._dirPrev = -1;
    this._par = { amp: 0, psi: 0 };
    this._build = 0; this._dropT = -1; this._lastDropT = -99;
    this._flash = 0; this._phPrev = 0; this._dirActive = 0; this._Sabs = M.id();
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
    this._lastTilingChange = 0;
    this._paletteShift = 0;
    this._log = [];
  }

  _rnd() { this._seed = (Math.imul(this._seed, 1664525) + 1013904223) >>> 0; return this._seed / 4294967296; }
  _note(e) { e.t = +this._time.toFixed(2); this._log.push(e); if (this._log.length > 30) this._log.shift(); }

  _setTiling(pq) {
    this._G = tilingGeometry(pq[0], pq[1]);
    this._U.uTil.value.set(this._G.p, this._G.c, this._G.r, this._G.rhoIn);
    this._U.uFlipK.value = 1.5 * this._G.D;
    this._dir = this._dir % this._G.p;
  }
  _pickTiling() {
    if (this._forceTiling) return null;
    let k = Math.floor(this._rnd() * TILINGS.length);
    if (TILINGS[k][0] === this._G.p && TILINGS[k][1] === this._G.q) k = (k + 1) % TILINGS.length;
    return TILINGS[k];
  }
  _startCrossfade() {
    const pq = this._pickTiling();
    if (!pq) return;
    this._next = tilingGeometry(pq[0], pq[1]);
    this._U.uTil2.value.set(this._next.p, this._next.c, this._next.r, this._next.rhoIn);
    this._mixT = 0;
    this._lastTilingChange = this._time;
    this._paletteShift = (this._paletteShift + 1) % 3;
    this._U.uMotifType.value = Math.floor(this._rnd() * 3);
    this._note({ what: 'tiling', p: pq[0], q: pq[1] });
  }

  _colours(profile, a) {
    const pal = profile.artworkPalette;
    const src = (pal && (pal.vividColors || pal.dominantColors)) || [];
    const hsl = [];
    for (let k = 0; k < 3; k++) {
      const c = _tone[k];
      if (src.length) vdjColor(src[(k + this._paletteShift) % src.length], c); else c.setHSL((0.55 + k * 0.33 + this._paletteShift * 0.1) % 1, 0.9, 0.55);
      const o = { h: 0, s: 0, l: 0 }; c.getHSL(o); hsl.push(o);
    }
    for (let k = 1; k < 3; k++) for (let j = 0; j < k; j++) { const d = Math.abs(hsl[k].h - hsl[j].h); if (Math.min(d, 1 - d) < 0.25) { hsl[k].h = (hsl[0].h + k / 3) % 1; _tone[k].setHSL(hsl[k].h, 0.9, 0.55); } }
    this._U.uTone0.value.copy(_tone[0]); this._U.uTone1.value.copy(_tone[1]); this._U.uTone2.value.copy(_tone[2]);
    this._U.uMean.value.copy(_tone[0]).lerp(_tone[1], 0.5).lerp(_tone[2], 0.33);
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const playing = profile.playbackState === 'PLAYING' && !this._freeze;
    const a = this._adapt.update(playing ? dt : 0, profile);
    if (profile.trackId !== this._trackSeen) {
      this._trackSeen = profile.trackId;
      this._seed = hashStr(String(profile.title || '') + '|' + String(profile.artist || '')) || 7;
    }
    this._colours(profile, a);
    if (!playing) return;                                        // frozen, never black
    this._time += dt;
    this._clock.update(dt);
    const U = this._U, G = this._G;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    const beatS = 60 / bpm;

    // the beat clock: adapt's phase when the music is pulsed, a free 126 bpm otherwise
    const pulsed = a.pulse > 0.25;
    let beat = a.beat, phase;
    if (pulsed) phase = a.beatPhase;
    else { this._freePhase += dt / beatS; if (this._freePhase >= 1) { this._freePhase -= 1; beat = true; } phase = this._freePhase; }
    if (beat) this._beats++;
    const bar = a.bar || (!pulsed && beat && this._beats % 4 === 0);
    const phrase = a.phrase || (!pulsed && beat && this._beats % 16 === 0);

    // the climb: a known drop approaching (faster translations, the parabolic swirl)
    const director = profile.director;
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * Math.min(1, dt / 0.8);

    // the drop: flash, the disc turns inside out and back over one beat
    if (a.drop && this._time - this._lastDropT > 2 * beatS) {
      this._dropT = 0; this._lastDropT = this._time;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
      this._note({ what: 'drop' });
    }
    if (this._dropT >= 0) { this._dropT += dt; if (this._dropT > beatS) this._dropT = -1; }
    U.uFlip.value = this._dropT >= 0 ? Math.sin(Math.PI * this._dropT / beatS) : 0;

    // the bar: a new direction among the p neighbours; the phrase (or a long scene): a new tiling
    if (bar) { const p = G.p; let k = (this._dir + 1 + Math.floor(this._rnd() * (p - 1))) % p; this._dir = k; }
    if ((phrase || (this._clock.wrapped && this._time - this._lastTilingChange > 25)) && this._mixT < 0) this._startCrossfade();
    else if (phrase) this._Sabs = M.id();
    if (this._mixT >= 0) {
      this._mixT += dt / beatS;
      U.uMix.value = smoother(this._mixT);
      if (this._mixT >= 1) {
        this._G = this._next; this._next = null; this._mixT = -1;
        U.uTil.value.copy(U.uTil2.value); U.uMix.value = 0;
        U.uFlipK.value = 1.5 * this._G.D; this._dir = this._dir % this._G.p;
        this._Sabs = M.id();                                   // a new tiling: new absolute frame
      }
    }

    // the translation of one tile per beat (two per beat high in the climb), with easing
    const fast = this._build > 0.5;
    const ph = fast ? (phase * 2) % 1 : phase;
    const Gc = this._G;
    // at the wrap the finished translation is a symmetry of the tiling: it is folded into the
    // absolute frame (tones travel with their tiles) and replaced by the identity, no jump
    if (ph < this._phPrev - 0.5) {
      const thA = TAU * this._dirActive / Gc.p;
      const S = M.mul(M.rot(Gc.rot), M.trans([Gc.nb * Math.cos(thA), Gc.nb * Math.sin(thA)]));
      // the absolute frame is re-anchored on the bar (a cut on the bar, party preset): after a
      // few beats the disc model squeezes every current tile into a speck near the rim and the
      // tones all fall on the same value
      this._Sabs = bar ? M.id() : M.mul(this._Sabs, S);
      this._dirActive = this._dir;
      this._flash = 1;
    }
    this._phPrev = ph;
    const s = smoother(ph);
    const th = TAU * this._dirActive / Gc.p;
    const am = Math.tanh(s * Math.atanh(Gc.nb));
    const aPt = [am * Math.cos(th), am * Math.sin(th)];
    let Mob = M.mul(M.rot(Gc.rot * s), M.trans(aPt));
    // the tile arriving at the centre: the one the full translation sends there, -R(a)
    const thArr = th + Math.PI + Gc.rot;
    U.uArr.value.set(Gc.nb * Math.cos(thArr), Gc.nb * Math.sin(thArr));
    // the parabolic glide in the climb, toward a boundary point fixed on the bar
    const parAmp = 0.9 * Math.max(0, this._build - 0.2) / 0.8 * (this._dropT >= 0 ? 1 - this._dropT / beatS : 1);
    this._par.amp += (parAmp - this._par.amp) * Math.min(1, dt / 0.5);
    if (bar) this._par.psi = this._rnd() * TAU;
    if (this._par.amp > 1e-3) Mob = M.mul(Mob, M.parabolic(this._par.amp * Math.sin(this._time * 0.9), this._par.psi));
    U.uMa.value.set(Mob[0][0], Mob[0][1]); U.uMb.value.set(Mob[1][0], Mob[1][1]);
    U.uMc.value.set(Mob[2][0], Mob[2][1]); U.uMd.value.set(Mob[3][0], Mob[3][1]);
    U.uIa.value.set(Mob[3][0], Mob[3][1]); U.uIb.value.set(-Mob[1][0], -Mob[1][1]);
    U.uIc.value.set(-Mob[2][0], -Mob[2][1]); U.uId.value.set(Mob[0][0], Mob[0][1]);
    const Sa = this._Sabs;
    U.uSa.value.set(Sa[0][0], Sa[0][1]); U.uSb.value.set(Sa[1][0], Sa[1][1]);
    U.uSc.value.set(Sa[2][0], Sa[2][1]); U.uSd.value.set(Sa[3][0], Sa[3][1]);

    // the beat: the arriving tile's edges flash, the rim pulses, the RGB split
    this._flash *= Math.pow(0.5, dt / (0.12 * beatS));
    U.uFlash.value = this._flash * (0.4 + 0.6 * a.pulse);
    U.uRingPulse.value = Math.exp(-ph * 4) * (0.3 + 0.7 * a.pulse);
    const b = a.bands;
    U.uBands.value.set(b ? b.low : 0.3 + 0.3 * a.intensity, b ? b.mid : 0.3, b ? b.high : 0.2 + 0.4 * a.intensity);
    U.uT.value = this._time;
    U.uGain.value = Math.min(1, a.lumaCap / 0.40);
    this._post.update(vdjBeatChroma(ph, beatS));
  }

  dispose() {
    if (this._scene && this._mesh) { this._mesh.onBeforeRender = null; this._scene.remove(this._mesh); }
    if (this._mesh) { this._mesh.geometry.dispose(); this._mesh.material.dispose(); }
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__poincare === this) window.__poincare = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _tone = [new THREE.Color(), new THREE.Color(), new THREE.Color()];
